import { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import Landing from "./pages/Landing";
import NotFound from "./pages/NotFound";
import Header from "./components/Header";
import SeasonalBanner from "./components/SeasonalBanner";
import SeasonalEffects from "./components/SeasonalEffects";
import useSeason from "./hooks/useSeason";
import Footer from "./components/Footer";
import { useAuth } from "./context/AuthContext";

// Only the landing page ships in the first download; every other page is
// fetched the first time it is opened.
const Templates = lazy(() => import("./pages/Templates"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const EventEditor = lazy(() => import("./pages/EventEditor"));
const EventManage = lazy(() => import("./pages/EventManage"));
const Invitation = lazy(() => import("./pages/Invitation"));
const Demo = lazy(() => import("./pages/Demo"));
const Account = lazy(() => import("./pages/Account"));
const Legal = lazy(() => import("./pages/Legal"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const GoogleReturn = lazy(() => import("./pages/GoogleReturn"));

const Loading = () => (
  <div className="page-loading" role="status" aria-label="Loading">
    <span />
  </div>
);

// Site chrome shared by every page except the guest-facing invitation.
function Layout({ children }) {
  const { pathname } = useLocation();
  const {
    season,
    showBanner,
    showEffects,
    showAmbient,
    ambientDensity,
    stickyBanner,
    headerAccent,
    dismiss,
  } = useSeason();

  // Once the page scrolls, the seasonal banner slims down to a compact strip.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // A new page starts at its top, not wherever the previous one was scrolled.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="shell">
      {showAmbient && (
        <SeasonalEffects
          effect={season.effect}
          className="season-fx--ambient"
          density={ambientDensity}
        />
      )}
      {/* The banner and header stick to the top together while scrolling. */}
      <div
        className={`topbar${stickyBanner && showBanner ? " topbar--sticky" : ""}${scrolled ? " is-compact" : ""}`}
      >
        {showBanner && (
          <SeasonalBanner
            season={season}
            onDismiss={dismiss}
            effects={showEffects}
          />
        )}
        <Header season={headerAccent ? season : null} />
      </div>
      {/* Keyed by path so each page fades in as it is opened. */}
      <main key={pathname} className="shell__main page-enter">
        {children}
      </main>
      <Footer />
    </div>
  );
}

// Host-only pages. The page the visitor wanted is kept in router state so
// login/registration can send them straight back to it.
function RequireAuth({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <Loading />;
  if (!user)
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  return children;
}

const host = (page) => (
  <RequireAuth>
    <Layout>{page}</Layout>
  </RequireAuth>
);

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* Public site */}
          <Route
            path="/"
            element={
              <Layout>
                <Landing />
              </Layout>
            }
          />
          <Route
            path="/templates"
            element={
              <Layout>
                <Templates />
              </Layout>
            }
          />
          <Route
            path="/login"
            element={
              <Layout>
                <Login />
              </Layout>
            }
          />
          <Route
            path="/register"
            element={
              <Layout>
                <Register />
              </Layout>
            }
          />

          {/* Links in the emails the API sends (the token is after "#") */}
          <Route
            path="/forgot-password"
            element={
              <Layout>
                <ForgotPassword />
              </Layout>
            }
          />
          <Route
            path="/reset-password"
            element={
              <Layout>
                <ResetPassword />
              </Layout>
            }
          />
          <Route
            path="/auth/google"
            element={
              <Layout>
                <GoogleReturn />
              </Layout>
            }
          />
          <Route
            path="/verify-email"
            element={
              <Layout>
                <VerifyEmail />
              </Layout>
            }
          />

          {/* Host workspace */}
          <Route path="/dashboard" element={host(<Dashboard />)} />
          <Route path="/create" element={host(<EventEditor />)} />
          <Route path="/events/:id" element={host(<EventManage />)} />
          <Route path="/events/:id/edit" element={host(<EventEditor />)} />
          <Route path="/account" element={host(<Account />)} />

          {/* The link guests receive */}
          <Route path="/i/:slug" element={<Invitation />} />
          {/* A template with sample content, full screen */}
          <Route path="/demo/:templateId" element={<Demo />} />

          {/* Policies */}

          <Route
            path="/terms"
            element={
              <Layout>
                <Legal page="terms" />
              </Layout>
            }
          />

          <Route
            path="/privacy"
            element={
              <Layout>
                <Legal page="privacy" />
              </Layout>
            }
          />

          <Route
            path="/refunds"
            element={
              <Layout>
                <Legal page="refunds" />
              </Layout>
            }
          />

          <Route
            path="/cancellations"
            element={
              <Layout>
                <Legal page="cancellations" />
              </Layout>
            }
          />

          <Route
            path="*"
            element={
              <Layout>
                <NotFound />
              </Layout>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
